import React, { useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { CVRoasterResult, CVQuality } from '../../../api/cv-roaster/types'
import PdfViewer from '../pdf'
import { useI18N } from '../../../libraries/i18n/hook'
import PlusIcon from '../../../assets/icons/plus.svg?react'
import Button from '../form/Button'
import Translate from '../../../libraries/i18n/Translate'
import ResultSection, { ulClassName, liClassName, linkClassName } from '../ai/ResultSection'
import QualityChip from './CVQualityChip'
import CvFeedback from '../../containers/user-feedback/CvFeedback'

interface CVResultsProps {
  result: CVRoasterResult
  newButton: { label: string, onClick: () => void }
  file?: File | null
  title: string
  feedbackTitle: string
  jobOfferId?: number
}

const CVResults: React.FC<CVResultsProps> = ({ result, newButton, title, feedbackTitle, jobOfferId }) => {
  const resultsRef = useRef<HTMLDivElement>(null)
  const { t } = useI18N()

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [result])

  const NewCVButton = (
    <Button className='flex' icon={<PlusIcon />} variant='primary' outlined onClick={newButton.onClick} size='sm'>
      {newButton.label}
    </Button>
  )
  const getOpacity = (level: CVQuality) => level === result?.analysis?.CV_quality ? 'opacity-100' : 'opacity-30'
  return (
    <div ref={resultsRef} className='mt-3 md:mt-4 bg-white py-4 md:p-6'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6'>
        {/* Analysis panel - left side */}
        <div className='border-r-2 border-soft-green pe-0 lg:border-solid lg:pe-6'>
          <div className='flex items-center justify-between mb-3 md:mb-6'>
            <h2 className='leading-none'>{title}</h2>
            {NewCVButton}
          </div>
          <div className='mb-4 md:mb-6 flex gap-x-4'>
            <div className={getOpacity(CVQuality.high)}><QualityChip level={CVQuality.high} /></div>
            <div className={getOpacity(CVQuality.mid)}><QualityChip level={CVQuality.mid} /></div>
            <div className={getOpacity(CVQuality.low)}><QualityChip level={CVQuality.low} /></div>
          </div>

          <ResultSection title={feedbackTitle}>
            {result.analysis.points_of_improvement.length > 0 ? (
              <ul className={ulClassName}>
                {result.analysis.points_of_improvement.map((point, index) => (
                  <li key={`point-${index}`} className={liClassName}>{point}</li>
                ))}
              </ul>
            ) : (
              <p className='text-gray-700 text-sm md:text-base'>{t('cv.no.improvement.points')}</p>
            )}
          </ResultSection>
          <div className='text-end'>
            <CvFeedback cvId={result.cvId} jobOfferId={jobOfferId} />
          </div>
          <ResultSection title={t('cv.analysis.resources')}>
            <ul className={ulClassName}>
              <li className={liClassName}>
                <Translate i18nKey='cv.analysis.resources.templates'>
                  Te recomendamos que utilices este <a className={linkClassName} rel='noreferrer' target='_blank' href='https://zrive-platform-content.s3.eu-west-1.amazonaws.com/public/bloom_cv_templates.zip'>template</a> que ha sido validado por Zrive.
                </Translate>
              </li>
              <li className={liClassName}>
                <Translate i18nKey='cv.analysis.resources.video'>
                  Puedes ver este <Link target='_blank' className={linkClassName} to='/content/videos/158'>video</Link> en el que explicamos todas las claves de un buen CV.
                </Translate>
              </li>
              <li className={liClassName}>
                <Translate i18nKey='cv.analysis.resources.document'>
                  En este <Link target='_blank' className={linkClassName} to='/content/documents/12'>documento</Link> podrás encontrar las principales partes en las que se fijan las empresas.
                </Translate>
              </li>
            </ul>
          </ResultSection>
        </div>

        {/* PDF Viewer - right side */}
        <div className='md:h-full mt-4 lg:mt-0 overflow-hidden'>
          {result && <PdfViewer pdfFile={result.cvUrl} hideThumbnails={true} />}
        </div>
      </div>
    </div>
  )
}

export default CVResults
